import type {
  CreateBusinessPayload,
  InvitationPreview,
  PendingInvitation,
  SessionProfile,
  TenantAccess,
} from "@/lib/api/types"
import { mainPb, tenantPb } from "@/lib/pb/clients"
import { protectedFileUrl } from "@/lib/pb/file-token"
import { authenticateMain, ensureMainSession } from "@/lib/pb/session"

/**
 * Everything the sign-in flow asks MainApp for.
 *
 * All of it is directory work — who you are, which businesses you belong to,
 * and how to get into a new one — so none of it touches a tenant's server.
 */

/**
 * The businesses this account belongs to.
 *
 * Comes back with the sign-in response rather than from a list endpoint, so
 * asking for it re-establishes the MainApp session as a side effect.
 */
export async function fetchTenants(): Promise<TenantAccess[]> {
  return authenticateMain()
}

/** Invitations waiting for this account's email address. */
export async function fetchMyInvitations(): Promise<PendingInvitation[]> {
  await ensureMainSession()
  return mainPb.send<PendingInvitation[]>("/api/my-invitations", { method: "GET" })
}

/**
 * Redeems an invitation and returns the business it let the user into.
 *
 * The code is the credential, so nothing else identifies the invitation.
 */
export async function joinBusiness(code: string): Promise<TenantAccess> {
  await ensureMainSession()
  return mainPb.send<TenantAccess>("/api/join", { method: "POST", body: { code } })
}

/**
 * Says no to an invitation. The code is the credential, as it is for joining,
 * so this works signed out: somebody who does not want the job is not made to
 * create an account to say so.
 */
export async function declineInvitation(code: string): Promise<void> {
  await mainPb.send("/api/decline-invitation", { method: "POST", body: { code } })
}

/**
 * Who an invitation is from and whether it still stands, for the page its
 * email links to. MainApp answers one 400 for a code that is wrong, withdrawn
 * or out of time, so nothing here can tell those apart.
 */
export async function previewInvitation(code: string): Promise<InvitationPreview> {
  return mainPb.send<InvitationPreview>(
    `/api/invitation-preview?code=${encodeURIComponent(code)}`,
    { method: "GET" },
  )
}

/**
 * Creates a business and makes the caller its admin.
 *
 * MainApp picks which Application server it lands on, so the returned access
 * is the only way to know where its data now lives.
 */
export async function createBusiness(
  payload: CreateBusinessPayload,
): Promise<TenantAccess> {
  await ensureMainSession()
  // Where the business signed up, for attribution. The app sends "mobile".
  return mainPb.send<TenantAccess>("/api/tenants", {
    method: "POST",
    body: { ...payload, signupSource: "web" },
  })
}

/**
 * Businesses the dashboard can actually be used for.
 *
 * A customer-only membership is hidden because customers book through the
 * public site and have nothing to manage here, and NO_ACCESS is a membership
 * that has been switched off without being deleted.
 */
export function staffTenants(tenants: TenantAccess[]): TenantAccess[] {
  return tenants.filter(
    (tenant) =>
      (tenant.capacity === "staff" || tenant.capacity === "both") &&
      tenant.role !== "NO_ACCESS" &&
      Boolean(tenant.serverUrl),
  )
}

/**
 * Who the user is inside a business, assembled from the session rather than
 * fetched.
 *
 * The tenant server's user record is a projection of the MainApp account,
 * refreshed on every sign-in, and the business's own settings arrived with the
 * tenant list — so there is nothing left to ask for.
 */
export function profileFor(tenant: TenantAccess): SessionProfile | null {
  const record = tenantPb.authStore.record
  if (!record) return null

  // One `name` is stored, because that is all Firebase reliably provides.
  const [firstName, ...rest] = String(record.name ?? "").trim().split(/\s+/)

  return {
    user: {
      id: record.id,
      first_name: firstName ?? "",
      last_name: rest.join(" "),
      email: (record.email as string) || null,
      phone: (record.phone as string) || null,
      image: record.avatar ? protectedFileUrl(record, record.avatar as string) : null,
    },
    permission: tenant.role,
    tenant: tenant.id,
    timezone: tenant.timezone,
    currency: tenant.currency,
    default_calendar: null,
  }
}
